import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  listDetalleMesas,
  listVentasCocina,
  createVentaCocina,
  updateVentaCocina,
  createVentaMesa,
  updateVentaMesa
} from "../api/ventasCocina";
import { listCategorias } from "../api/categorias";
import { listProductos } from "../api/productos";
import { listClientLazy } from "../api/clientesReceptor";
import { FacturacionFe, FacturacionTicket } from "../api/ventas";
import { facturasCliente } from "../api/facturar";
import { showAlertError, showAlertExito, showConfirmAlert } from "../utils/alertHelper";
import Loader from "./Loader";
import {
  FiLayout, FiPlus, FiArrowLeft, FiShoppingCart,
  FiCheckCircle, FiUser, FiInfo, FiTrash2,
  FiZap, FiCoffee, FiPieChart, FiUserPlus
} from "react-icons/fi";
import ReactSelect from "react-select";

const TAX_MAP = {
  "20": { rate: 0.13, desc: "Impuesto al Valor Agregado 13%" },
  "C3": { rate: 0, desc: "Impuesto al Valor Agregado (exportaciones) 0%" },
  "59": { rate: 0.05, desc: "Turismo: por alojamiento (5%)" },
  "71": { fixed: 7, desc: "Turismo: salida del país por vía aérea $7.00" },
};

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const fmt = (n) => (round2(Number(n) || 0)).toFixed(2);

const VentasCocina = () => {
  const [loading, setLoading] = useState(true);
  const [mesas, setMesas] = useState([]);
  const [ventasCocina, setVentasCocina] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tipoDocumentos, setTipoDocumentos] = useState([]);

  // Estados de vista
  const [mesaSeleccionada, setMesaSeleccionada] = useState(null); // id de mesa
  const [ordenActual, setOrdenActual] = useState(null); // objeto de ventaCocina si existe
  const [categoriaActiva, setCategoriaActiva] = useState(null);
  const [carrito, setCarrito] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [tipoFactura, setTipoFactura] = useState("");

  const [showMesaModal, setShowMesaModal] = useState(false);
  const [nuevaMesa, setNuevaMesa] = useState({ numero_mesa: "", cantidad_personas: "" });
  const [buscar, setBuscar] = useState("");
  const [showMobileCart, setShowMobileCart] = useState(false);

  const { idMesa } = useParams();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resMesas, resVentas, resCats, resProds, resDocs, resClientes] = await Promise.all([
        listDetalleMesas(),
        listVentasCocina(),
        listCategorias(),
        listProductos({ status: 1 }), // Traer todos los activos
        facturasCliente(),
        listClientLazy(1, 100, "") // Para seleccionar cliente si es necesario
      ]);

      const currentMesas = resMesas?.data || [];
      const currentVentas = resVentas?.data || [];
      const currentCats = resCats?.data || [];
      const currentProds = resProds?.data || [];
      const currentDocs = resDocs?.data || [];
      const currentClients = resClientes?.data || [];

      setMesas(currentMesas);
      setVentasCocina(currentVentas);
      setCategorias(currentCats);
      setProductos(currentProds);
      const allowedCodes = ["88", "01", "03"];
      const sortedDocs = currentDocs
        .filter(d => {
          const t = d.TipoDocumento || d.typeFact || d;
          return allowedCodes.includes(t.codigo);
        })
        .sort((a, b) => {
          const codeA = (a.TipoDocumento || a.typeFact || a).codigo;
          const codeB = (b.TipoDocumento || b.typeFact || b).codigo;
          return allowedCodes.indexOf(codeA) - allowedCodes.indexOf(codeB);
        });

      setTipoDocumentos(sortedDocs);
      setClientes(currentClients);

      if (currentCats.length > 0) {
        setCategoriaActiva(currentCats[0].id);
      }

      // Default tipo factura: Ticket (88) if present, else Factura (01), else first
      if (sortedDocs.length > 0) {
        const ticketDoc = sortedDocs.find(d => (d.TipoDocumento || d.typeFact || d).codigo === "88");
        const facturaDoc = sortedDocs.find(d => (d.TipoDocumento || d.typeFact || d).codigo === "01");
        
        if (ticketDoc) {
          setTipoFactura("88");
        } else if (facturaDoc) {
          setTipoFactura("01");
        } else {
          setTipoFactura((sortedDocs[0].TipoDocumento || sortedDocs[0].typeFact || sortedDocs[0]).codigo);
        }
      }

      // Auto-seleccionar mesa si viene por URL
      if (idMesa) {
        const m = currentMesas.find(mes => String(mes.id) === String(idMesa));
        if (m) {
          setMesaSeleccionada(m);
          // Buscar la venta activa DIRECTAMENTE de los datos recién traídos
          const ventaActiva = currentVentas.find(v => String(v.id_mesa) === String(m.id) && v.status !== 3);
          if (ventaActiva) {
            setOrdenActual(ventaActiva);
            try {
              const jsonOrder = JSON.parse(ventaActiva.json);
              setCarrito((jsonOrder.items || []).map(it => ({ ...it, estado: it.estado || 1 })));
              if (jsonOrder.idCliente) {
                const c = currentClients.find(cl => cl.id === jsonOrder.idCliente);
                if (c) setClienteSeleccionado({ value: c.id, label: c.nombre });
              }
            } catch (e) {
              console.error("Error al parsear pedido:", e);
              setCarrito([]);
            }
          }
        }
      }

    } catch (error) {
      console.error("Error fetching data for VentasCocina:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSelectMesa = (mesa) => {
    setMesaSeleccionada(mesa);
    // Buscar si ya tiene una venta activa
    const ventaActiva = ventasCocina.find(v => String(v.id_mesa) === String(mesa.id) && v.status !== 3);
    if (ventaActiva) {
      setOrdenActual(ventaActiva);
      try {
        const jsonOrder = JSON.parse(ventaActiva.json);
        const items2 = (jsonOrder.items || []).map(it => ({ ...it, estado: it.estado || 1 }));
        setCarrito(items2);
        if (jsonOrder.idCliente) {
          const c = clientes.find(cl => cl.id === jsonOrder.idCliente);
          if (c) setClienteSeleccionado({ value: c.id, label: c.nombre });
        }
      } catch (e) {
        setCarrito([]);
      }
    } else {
      setOrdenActual(null);
      setCarrito([]);
      setClienteSeleccionado(null);
    }
  };

  const agregarAlCarrito = (prod) => {
    setCarrito(prev => {
      const existe = prev.find(item => item.id === prod.id);
      if (existe) {
        return prev.map(item => item.id === prod.id ? { ...item, cantidad: item.cantidad + 1, estado: 1 } : item);
      }
      return [...prev, {
        id: prod.id,
        name: prod.name,
        price: prod.price,
        cantidad: 1,
        estado: 1, // 1: Pendiente, 2: Servido
        note: "", // Nota visual opcional
        // Datos necesarios para la venta formal posterior
        sku: prod.sku,
        id_unidad_medida: prod.id_unidad_medida,
        id_type_item: prod.id_type_item,
        id_tributo: prod.id_tributo,
        UnidadMedida: prod.UnidadMedida,
        Tributo: prod.Tributo,
        TipoItem: prod.TipoItem
      }];
    });
  };

  const actualizarCantidad = (id, delta) => {
    setCarrito(prev => prev.map(item => {
      if (item.id === id) {
        const nuevaCant = Math.max(0, item.cantidad + delta);
        return { ...item, cantidad: nuevaCant };
      }
      return item;
    }).filter(item => item.cantidad > 0));
  };

  const actualizarNota = (id, text) => {
    setCarrito(prev => prev.map(item => item.id === id ? { ...item, note: text } : item));
  };

  const totalCarrito = carrito.reduce((acc, item) => acc + (item.price * item.cantidad), 0);

  const guardarPedido = async (nuevoEstado = 1) => {
    if (carrito.length === 0) return showAlertError("Carrito vacío", "Agregue productos al pedido.");

    const payload = {
      id_mesa: mesaSeleccionada.id,
      json: JSON.stringify({
        items: carrito,
        total: totalCarrito,
        idCliente: clienteSeleccionado?.value || null
      }),
      status: nuevoEstado
    };

    try {
      if (ordenActual) {
        await updateVentaCocina(ordenActual.id, payload);
      } else {
        await createVentaCocina(payload);
        // Actualizar estado de mesa a Utilizada (2) con payload completo
        await updateVentaMesa(mesaSeleccionada.id, {
          cantidad_personas: mesaSeleccionada.cantidad_personas,
          numero_mesa: mesaSeleccionada.numero_mesa,
          id_sucursal: mesaSeleccionada.id_sucursal,
          status: 2
        });
      }
      showAlertExito("Éxito", "Pedido guardado correctamente.");
      fetchData();
      navigate("/dashboard/mesas");
    } catch (e) {
      showAlertError("Error", "No se pudo guardar el pedido.");
    }
  };

  const completarVenta = async () => {
    if (!clienteSeleccionado) {
      return showAlertError("Cliente requerido", "Debe seleccionar un cliente para completar la facturación.");
    }

    const confirmar = await showConfirmAlert(
      "¿Completar Venta?",
      "Esta acción generará la factura formal y liberará la mesa."
    );
    if (!confirmar) return;

    // Mapear el carrito al formato de ItemsProducts de Ventas.jsx
    const itemsProducts = carrito.map((it, index) => {
      const hasIva = true; // Por defecto asumimos que tiene IVA
      const cantidad = Number(it.cantidad) || 0;
      const precioBruto = Number(it.price) || 0;
      const totalLinea = round2(cantidad * precioBruto);

      let ventaGravada = totalLinea;
      let ivaValor = 0;

      if (hasIva) {
        ivaValor = round2(totalLinea - (totalLinea / 1.13));
        ventaGravada = round2(totalLinea - ivaValor);
      }

      const precioUni = cantidad > 0 ? (ventaGravada / cantidad) : 0;

      const tributos = [{
        codigo: "20",
        descripcion: TAX_MAP["20"]?.desc || "",
        valor: fmt(ivaValor)
      }];

      return {
        numItem: index + 1,
        idProducto: it.id,
        tipoItem: it.id_type_item ? String(it.id_type_item) : "1",
        descripcion: it.name,
        cantidad: cantidad,
        uniMedida: it.id_unidad_medida ? String(it.id_unidad_medida) : "36",
        precioUni: round2(precioUni),
        montoDescu: 0,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ventaGravada: round2(ventaGravada),
        tributos: tributos,
        psv: 0,
        noGravado: 0
      };
    });

    const subtotalNum = itemsProducts.reduce((acc, it) => acc + (Number(it.ventaGravada) || 0), 0);
    const totalTributosNum = itemsProducts.reduce((acc, it) => {
      const sumTrib = it.tributos.reduce((s, t) => s + (Number(t.valor) || 0), 0);
      return acc + sumTrib;
    }, 0);

    const totalFinal = round2(subtotalNum + totalTributosNum);

    const payloadVenta = {
      typeFacts: tipoFactura || "01",
      idCliente: clienteSeleccionado.value,
      ItemsProducts: itemsProducts,
      impuestos: round2(totalTributosNum),
      total: totalFinal,
      tipoFacturacion: 1
    };

    try {
      setLoading(true);
      const res = tipoFactura === "88" 
        ? await FacturacionTicket(payloadVenta)
        : await FacturacionFe(payloadVenta);
      if (res) {
        // Marcar orden cocina como completada (3) y liberar mesa (1)
        await updateVentaCocina(ordenActual.id, {
          id_mesa: mesaSeleccionada.id,
          json: ordenActual.json,
          status: 3
        });
        await updateVentaMesa(mesaSeleccionada.id, {
          cantidad_personas: mesaSeleccionada.cantidad_personas,
          numero_mesa: mesaSeleccionada.numero_mesa,
          id_sucursal: mesaSeleccionada.id_sucursal,
          status: 1
        });
        showAlertExito("Éxito", "Venta finalizada y mesa liberada.");
        navigate("/dashboard/mesas");
      }
    } catch (e) {
      showAlertError("Error", "No se pudo completar la facturación.");
    } finally {
      setLoading(false);
    }
  };

  const crearMesaRef = async (e) => {
    e.preventDefault();
    try {
      await createVentaMesa(nuevaMesa);
      setShowMesaModal(false);
      setNuevaMesa({ numero_mesa: "", cantidad_personas: "" });
      fetchData();
    } catch (e) {
      showAlertError("Error", "No se pudo crear la mesa.");
    }
  };

  if (loading && mesas.length === 0) return <Loader />;

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden">

      {/* HEADER DE MÓDULO */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard/mesas")}
            className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center text-white shadow-lg shadow-orange-100 hover:bg-orange-600 transition-all"
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-xl font-bold text-slate-800 leading-none">Punto de Venta</h2>
            <p className="text-xs text-slate-400 mt-1">Gestión de productos y cuenta</p>
          </div>
        </div>
        {!idMesa && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-full uppercase">Seleccione una mesa para comenzar</span>
          </div>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">

        {/* PANEL IZQUIERDO: SELECCIÓN DE MESA O MENU */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-10 lg:p-16 relative custom-scrollbar bg-slate-50/50">

          {!mesaSeleccionada ? (
            /* VISTA DE TODAS LAS MESAS - DISEÑO PREMIUM ESPACIADO */
            <div className="max-w-6xl mx-auto animate-fade-in">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 md:mb-12">
                <div>
                  <h3 className="text-2xl md:text-3xl font-black text-slate-800 tracking-tight flex items-center gap-3">
                    <FiLayout className="text-orange-600" /> Selección de Mesa
                  </h3>
                  <p className="text-xs md:text-sm text-slate-400 font-medium mt-1 md:mt-2">Elija una mesa disponible o vea el detalle de una ocupada</p>
                </div>
                <div className="flex items-center justify-center gap-4 bg-white p-2 rounded-2xl shadow-sm border border-slate-100">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    Libres: {mesas.filter(m => m.status === 1).length}
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-rose-50 text-rose-600 text-[10px] font-black uppercase tracking-widest">
                    <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></div>
                    Ocupadas: {mesas.filter(m => m.status === 2).length}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-10">
                {mesas.map((m) => {
                  const venta = ventasCocina.find(v => v.id_mesa === m.id && v.status !== 3);
                  const isOccupied = m.status === 2 || !!venta;
                  return (
                    <div
                      key={m.id}
                      className={`group relative perspective bg-white rounded-[2rem] sm:rounded-[3rem] p-4 sm:p-10 border transition-all duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)] flex flex-col items-center justify-center
                        ${isOccupied
                          ? "border-rose-100 shadow-rose-50/50"
                          : "border-emerald-100 shadow-emerald-50/50"
                        }`}
                    >
                      {/* Indicador de Estado Flotante */}
                      <div className={`absolute top-2 sm:top-4 left-2 sm:left-4 flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-[9px] font-black uppercase tracking-tighter shadow-sm
                        ${isOccupied ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-600"}`}>
                        <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full ${isOccupied ? "bg-rose-500 animate-pulse" : "bg-emerald-500"}`}></div>
                        {isOccupied ? "Ocupada" : "Libre"}
                      </div>

                      <div className={`w-16 h-16 sm:w-24 sm:h-24 rounded-2xl sm:rounded-[2rem] flex items-center justify-center mb-3 sm:mb-6 transition-all duration-500 transform group-hover:scale-110 group-hover:rotate-6
                        ${isOccupied ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}
                      >
                        <span className="text-xl sm:text-4xl font-black italic">M{m.numero_mesa}</span>
                      </div>

                      <div className="text-center mb-3 sm:mb-6">
                        <div className="flex items-center justify-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-bold text-slate-400">
                          <FiUser size={12} /> {m.cantidad_personas} pax
                        </div>
                      </div>

                      {/* BOTÓN DE ACCIÓN DINÁMICO */}
                      <button
                        onClick={() => handleSelectMesa(m)}
                        className={`w-full py-2 sm:py-3 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-all shadow-lg active:scale-95
                          ${isOccupied
                            ? "bg-rose-600 text-white shadow-rose-100 hover:bg-rose-700"
                            : "bg-emerald-600 text-white shadow-emerald-100 hover:bg-emerald-700"
                          }`}
                      >
                        {isOccupied ? "Ver Orden" : "Tomar Pedido"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* VISTA DE MENÚ POS */
            <div className="flex flex-col animate-fade-in-up">
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={() => setMesaSeleccionada(null)}
                  className="p-2.5 rounded-full bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 transition-all shadow-sm"
                >
                  <FiArrowLeft size={20} />
                </button>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 leading-none">Mesa # {mesaSeleccionada.numero_mesa}</h3>
                  <p className="text-sm text-slate-400 mt-1">Seleccione productos para la orden</p>
                </div>
              </div>

              {/* BUSCADOR Y CATEGORÍAS */}
              <div className="flex flex-col gap-4 mb-6">
                <div className="relative">
                  <FiPlus className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 rotate-45" size={20} />
                  <input
                    type="text"
                    placeholder="Buscar por nombre o SKU..."
                    value={buscar}
                    onChange={(e) => setBuscar(e.target.value)}
                    className="w-full bg-white border-2 border-slate-100 rounded-2xl pl-12 pr-4 py-3.5 focus:border-indigo-400 outline-none transition-all font-bold text-slate-700 shadow-sm"
                  />
                </div>

                <div className="flex gap-2 overflow-x-auto pb-4 custom-scrollbar">
                  {categorias.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setCategoriaActiva(cat.id);
                        setBuscar(""); // Limpiar búsqueda al cambiar de categoría
                      }}
                      className={`px-6 py-3 rounded-2xl font-bold text-sm whitespace-nowrap transition-all border-2
                        ${categoriaActiva === cat.id && !buscar
                          ? "bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100"
                          : "bg-white border-slate-100 text-slate-500 hover:border-indigo-200 hover:text-indigo-500"
                        }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setCategoriaActiva(null);
                      setBuscar("");
                    }}
                    className={`px-6 py-3 rounded-2xl font-bold text-sm whitespace-nowrap transition-all border-2
                      ${!categoriaActiva && !buscar
                        ? "bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100"
                        : "bg-white border-slate-100 text-slate-500 hover:border-indigo-200 hover:text-indigo-500"
                      }`}
                  >
                    Todos
                  </button>
                </div>
              </div>

              {/* PRODUCTOS */}
              <div className="pt-4 pr-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {productos
                    .filter(p => {
                      const matchesCategory = !buscar ? (!categoriaActiva || p.id_category === categoriaActiva) : true;
                      const matchesSearch = !buscar ||
                        p.name.toLowerCase().includes(buscar.toLowerCase()) ||
                        (p.sku && p.sku.toLowerCase().includes(buscar.toLowerCase()));
                      return matchesCategory && matchesSearch;
                    })
                    .map(p => (
                      <button
                        key={p.id}
                        onClick={() => agregarAlCarrito(p)}
                        className="bg-white p-4 rounded-3xl border-2 border-slate-50 shadow-sm hover:shadow-md hover:border-indigo-100 transition-all flex flex-col text-left group active:scale-95 overflow-hidden relative"
                      >
                        {p.photo ? (
                          <div className="w-full h-32 rounded-2xl overflow-hidden mb-3 bg-slate-50">
                            <img
                              src={`${process.env.REACT_APP_RUTA_PRODUCTOS}${p.photo}`}
                              alt={p.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              onError={(e) => { e.target.onerror = null; e.target.src = ""; }}
                            />
                          </div>
                        ) : (
                          <div className="w-full h-32 rounded-2xl bg-indigo-50 flex items-center justify-center mb-3">
                            <FiCoffee className="w-10 h-10 text-indigo-200" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h4 className="font-bold text-slate-800 text-sm line-clamp-2 mb-1">{p.name}</h4>
                          <div className="flex items-center justify-between mt-auto">
                            <span className="text-indigo-600 font-extrabold">$ {p.price}</span>
                            <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-indigo-600 group-hover:text-white transition-colors shadow-inner">
                              <FiPlus size={16} />
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* BOTÓN FLOTANTE CARRITO (MÓVIL) */}
          {mesaSeleccionada && (
            <button
              onClick={() => setShowMobileCart(true)}
              className="lg:hidden fixed bottom-8 right-8 z-[100] bg-indigo-600 text-white p-5 rounded-full shadow-[0_20px_50px_rgba(79,70,229,0.4)] flex items-center gap-4 active:scale-90 transition-all animate-fade-in-up"
            >
              <div className="relative">
                <FiShoppingCart size={28} />
                {carrito.length > 0 && (
                  <span className="absolute -top-3 -right-3 bg-rose-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black border-2 border-white">
                    {carrito.reduce((acc, it) => acc + it.cantidad, 0)}
                  </span>
                )}
              </div>
              <span className="font-black text-lg tracking-tight">${totalCarrito.toFixed(2)}</span>
            </button>
          )}

        </div>

        {/* SIDEBAR DERECHO: ORDEN ACTUAL (Drawer en móvil) */}
        {mesaSeleccionada && (
          <>
            {/* Backdrop para móvil */}
            {showMobileCart && (
              <div
                className="lg:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110] animate-fade-in"
                onClick={() => setShowMobileCart(false)}
              />
            )}

            <aside className={`
              w-full lg:w-96 bg-white border-l border-slate-200 flex flex-col shadow-2xl fixed lg:static inset-y-0 right-0 z-[120] lg:z-10 transition-transform duration-500 overflow-y-auto custom-scrollbar
              ${showMobileCart ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
            `}>
              <div className="p-6 border-b border-slate-100 shrink-0 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-black text-slate-800 flex items-center gap-3">
                    <FiShoppingCart className="text-indigo-600" /> Orden en Curso
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="bg-indigo-50 text-indigo-600 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider"> Mesa {mesaSeleccionada.numero_mesa} </span>
                    <button
                      onClick={() => setShowMobileCart(false)}
                      className="lg:hidden p-2 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-all"
                    >
                      <FiPlus className="rotate-45" size={24} />
                    </button>
                  </div>
                </div>

                {/* Selección de Tipo de Factura y Cliente */}
                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-1 mb-1 block">Tipo de Documento</label>
                    <select
                      value={tipoFactura}
                      onChange={(e) => setTipoFactura(e.target.value)}
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-2.5 outline-none transition-all font-bold text-sm text-slate-700 focus:border-indigo-300"
                    >
                      {tipoDocumentos.map(doc => {
                        const type = doc.TipoDocumento || doc.typeFact || doc;
                        return (
                          <option key={doc.id} value={type.codigo}>{type.name}</option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest pl-1 mb-1 block">Cliente (Requerido para facturar)</label>
                    <ReactSelect
                      options={clientes.map(c => ({ value: c.id, label: c.nombre }))}
                      value={clienteSeleccionado}
                      onChange={setClienteSeleccionado}
                      placeholder="Seleccione Cliente..."
                      className="text-sm"
                      isSearchable
                      isClearable
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 p-4">
                {carrito.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-40">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                      <FiShoppingCart size={32} />
                    </div>
                    <p className="text-sm font-bold text-slate-500">No hay productos añadidos</p>
                    <p className="text-xs text-slate-400 mt-2">Selecciona productos del menú para comenzar</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {carrito.map(item => (
                      <div key={item.id} className="bg-slate-50 rounded-2xl p-4 flex items-center gap-3 border border-slate-100 transition-all hover:bg-white hover:shadow-sm">
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-bold text-slate-800 truncate mb-1">{item.name}</h5>
                          <span className="text-xs text-indigo-600 font-extrabold">$ {item.price}</span>
                          <div className="mt-1.5">
                            <input 
                              type="text"
                              value={item.note || ""}
                              onChange={(e) => actualizarNota(item.id, e.target.value)}
                              placeholder="Detalle (ej: sin cebolla)"
                              className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-[10px] text-slate-500 outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 transition-all font-medium"
                            />
                          </div>
                        </div>
                        <div className="flex items-center bg-white rounded-xl border border-slate-100 p-1 shadow-sm shrink-0">
                          <button
                            onClick={() => actualizarCantidad(item.id, -1)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-rose-50 hover:text-rose-600 transition-colors text-slate-400"
                          >
                            <FiTrash2 size={14} />
                          </button>
                          <span className="w-8 text-center text-sm font-black text-slate-700">{item.cantidad}</span>
                          <button
                            onClick={() => actualizarCantidad(item.id, 1)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-indigo-50 hover:text-indigo-600 transition-colors text-slate-400"
                          >
                            <FiPlus size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer del aside */}
              <div className="p-6 border-t border-slate-100 bg-slate-50/50 space-y-4 shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-black text-slate-400 uppercase tracking-widest">Total Estimado</span>
                  <span className="text-2xl font-black text-indigo-600">$ {totalCarrito.toFixed(2)}</span>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <button
                    onClick={() => guardarPedido(1)}
                    disabled={carrito.length === 0}
                    className="w-full bg-white border-2 border-slate-200 text-slate-700 py-3 rounded-2xl font-bold flex items-center justify-center gap-2 hover:border-indigo-300 hover:text-indigo-600 transition-all shadow-sm disabled:opacity-50"
                  >
                    <FiZap /> Enviar a Cocina
                  </button>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => guardarPedido(2)}
                      disabled={!ordenActual}
                      className="bg-white border-2 border-slate-200 text-slate-700 py-3 rounded-2xl font-bold text-xs flex flex-col items-center justify-center gap-1 hover:border-emerald-300 hover:text-emerald-600 transition-all shadow-sm disabled:opacity-50"
                    >
                      <FiCheckCircle size={18} /> <span>Servido</span>
                    </button>
                    <button
                      onClick={completarVenta}
                      disabled={!ordenActual}
                      className="bg-indigo-600 text-white py-3 rounded-2xl font-black text-xs flex flex-col items-center justify-center gap-1 hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 disabled:opacity-50"
                    >
                      <FiPieChart size={18} /> <span>Cobrar Mesa</span>
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </>
        )}
      </div>

      {/* MODAL CREAR MESA */}
      {showMesaModal && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-zoom-in">
            <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-indigo-600 text-white">
              <h4 className="font-extrabold text-lg flex items-center gap-2"><FiUserPlus /> Nueva Mesa</h4>
              <button onClick={() => setShowMesaModal(false)} className="hover:rotate-90 transition-transform"><FiPlus className="rotate-45" size={24} /></button>
            </div>
            <form onSubmit={crearMesaRef} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-black uppercase text-slate-400 mb-2">Número de Mesa</label>
                <input
                  type="number"
                  required
                  value={nuevaMesa.numero_mesa}
                  onChange={(e) => setNuevaMesa({ ...nuevaMesa, numero_mesa: e.target.value })}
                  className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-indigo-400 outline-none transition-all font-bold"
                />
              </div>
              <div>
                <label className="block text-xs font-black uppercase text-slate-400 mb-2">Capacidad (Personas)</label>
                <input
                  type="number"
                  required
                  value={nuevaMesa.cantidad_personas}
                  onChange={(e) => setNuevaMesa({ ...nuevaMesa, cantidad_personas: e.target.value })}
                  className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-4 py-3 focus:border-indigo-400 outline-none transition-all font-bold"
                />
              </div>
              <button type="submit" className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all mt-4">
                Registrar Mesa
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default VentasCocina;
